import React from "react";

import ReceiptIcon from "@material-ui/icons/Receipt";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { useTheme } from "@/src/contexts/ThemeContext";

import PasswordValidator from "../common/form/PasswordValidator";
import Input from "../form/Input";
import Label from "../form/Label";
import RadioList from "../form/RadioList";
import ReadOnlyInput from "../form/ReadOnlyInput";

import type { JSX } from "react";

import type { AppState } from "../../reducers";

const PASSWORD_RULES = ["min8", "lowercase", "uppercase", "digits"];

function PersonalInformationsForm(): JSX.Element {
  const { watch } = useFormContext();

  const { t } = useTranslation("profile");

  const { preference, setPreference } = useTheme();

  const user = useSelector((state: AppState) => state.fetchReducer.user.data);

  const newPassword = watch("newPassword");

  const themeOptionList = [
    { value: "system", label: t("themeSystem") },
    { value: "light", label: t("themeLight") },
    { value: "dark", label: t("themeDark") },
  ];

  const handleThemeChange = (themeValue: string): void => {
    if (
      themeValue === "system" ||
      themeValue === "light" ||
      themeValue === "dark"
    ) {
      setPreference(themeValue);
    }
  };

  return (
    <div className="bg-card flex w-full gap-[1.875rem] rounded-[4px] py-[3.75rem] shadow">
      <div className="flex basis-[30%] items-center justify-center">
        <Input name="avatar" type="avatar" />
      </div>
      <div className="flex w-[390px] flex-col gap-[2.5rem] px-[0.625rem]">
        <div className="flex flex-col">
          <Input
            isCompact
            label={t("lastnameLabel")}
            name="lastName"
            placeholder={t("lastnameContent")}
          />
          <Input
            isCompact
            label={t("firstnameLabel")}
            name="firstName"
            placeholder={t("firstnameContent")}
          />
          <ReadOnlyInput
            isCompact
            label={t("emailLabel")}
            name="email"
            value={user.email}
          />
          <ReadOnlyInput
            isCompact
            label={t("organizationLabel")}
            name="organization"
            value={user.organization_label}
          />
        </div>
        <div className="flex flex-col">
          <Input
            isCompact
            label={t("oldPasswordLabel")}
            name="currentPassword"
            type="password"
          />
          <Input
            isCompact
            label={t("newPasswordLabel")}
            name="newPassword"
            type="password"
          />
          <div className="flex flex-wrap">
            <PasswordValidator
              className="basis-1/2 py-[0.3125rem]"
              password={newPassword}
              rules={PASSWORD_RULES}
            />
          </div>
        </div>
        <div className="flex flex-col gap-[0.625rem]">
          <Label isCompact label={t("themeLabel")} name="theme" />
          <RadioList
            defaultChecked={preference}
            name="theme"
            onChange={handleThemeChange}
            optionList={themeOptionList}
          />
        </div>
        {user.invoice_url ? (
          <div className="flex w-full items-center justify-center">
            <a
              href={user.invoice_url}
              rel="noopener noreferrer"
              target="_blank"
            >
              <ReceiptIcon /> {t("admin-profile:go_to_invoice_url")}
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default PersonalInformationsForm;
